"use client";

import * as React from "react";

import { cn } from "@/lib/utils";

export type FieldShellProps = React.ComponentProps<"div"> & {
  /** Floats up into a gap in the outline once the control is focused or filled. */
  label?: React.ReactNode;
  /**
   * Forces the floated state for controls whose emptiness can't be read with
   * `:placeholder-shown` — date inputs, selects, composed widgets.
   */
  filled?: boolean;
  invalid?: boolean;
  /**
   * Dims the whole field. Only needed for composed controls that convey
   * disabled some other way — a real `:disabled` on the control is detected.
   */
  disabled?: boolean;
  /** Sits at the trailing edge inside the outline (hint icons, unit chips). */
  adornment?: React.ReactNode;
  /** Ties the label to its control; the control must carry the same id. */
  htmlFor?: string;
  className?: string;
  children: React.ReactNode;
};

/**
 * The outlined-field chrome, in the Material shape: a 1px stroke that thickens
 * to 2px in the accent colour on focus, with the label floating from inside
 * the box up into a notch cut through the top of that stroke.
 *
 * Chrome only — it draws no control. The child is the control and must carry
 * `field-control`, which strips its own border so this outline is the only
 * stroke; see the `.field-shell` block in globals.css for how the notch and
 * the float are built.
 *
 * The legend renders even with no label so the outline geometry is identical
 * either way: a fieldset's top border is positioned off its legend, so
 * dropping the legend would lift the stroke off the control's edge.
 */
export function FieldShell({
  label,
  filled,
  invalid,
  disabled,
  adornment,
  htmlFor,
  className,
  children,
  // Anything else lands on the root div — including a `ref`, which React 19
  // passes as an ordinary prop. Without this, a shell handed to a Radix
  // `asChild` trigger silently eats the onClick and ref it was cloned with,
  // and the control simply never opens. Put the shell OUTSIDE such a trigger
  // where you can; this is the backstop for when it ends up inside one.
  ...rest
}: FieldShellProps) {
  return (
    <div
      className={cn("field-shell", className)}
      data-filled={filled ? "true" : undefined}
      data-invalid={invalid ? "true" : undefined}
      data-disabled={disabled ? "true" : undefined}
      {...rest}
    >
      {children}

      {label ? (
        <label className="field-label" htmlFor={htmlFor}>
          {label}
        </label>
      ) : null}

      {adornment ? <span className="field-adornment">{adornment}</span> : null}

      {/* Hidden from assistive tech: the legend repeats the label purely to
          reserve the width of the gap. */}
      <fieldset className="field-outline" aria-hidden="true">
        <legend className="field-notch">
          <span>{label}</span>
        </legend>
      </fieldset>
    </div>
  );
}

export default FieldShell;
